/**
 * 見積書の作成・編集画面 (2026-10-16 全面改修)。
 *
 * 顧客に送る見積書 (PublicQuotePage) と同じ見た目のまま、その場で編集する全画面のエディタ。
 *  - 件名・品目名・数量・単価・割引は書類の上で直接入力。未入力の欄は点線の枠で示す
 *  - 発行日・有効期限はクリックでカレンダー / yyyy/mm/dd 入力
 *  - 請求先住所・納品先住所・品目 (製品リスト)・支払方法/下取り/備考 は右から出るパネルで入力
 *  - Enter (日本語の変換を確定する Enter は除く) / 欄の外をクリック / 別の欄をクリック で値を確定し、
 *    そのたびに下書きを保存する (新規作成は品目が 1 件そろった時点で createQuote、以降は updateQuoteDraft)
 *  - 「メール送信」で保存済みの下書きのメール送信画面 (DocumentMailDialog) を開く
 *
 * 呼び出し元 (DealQuotesTab / DealBriefingTab / ContactDealsTab) との約束 (Props) は従来どおり。
 */
import type {
  BillingAddress,
  ConsideredProductItem,
  DeliveryAddress,
  Quote,
} from '@goenhub/shared';
import {
  CATEGORY_LOGO_POSITION_DEFAULT,
  buildLegacyAddressFields,
  getBillingAddresses,
} from '@goenhub/shared';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lock, Trash2 } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
} from 'react';
import { toast } from 'sonner';

import { AddressPanel } from './quote-editor/AddressPanel';
import { CommitInput } from './quote-editor/CommitInput';
import { DatePickerPopover } from './quote-editor/DatePickerPopover';
import {
  DELIVERY_SAME_AS_BILLING,
  addDaysIso,
  addressLine,
  addressSummary,
  computeTotals,
  emptyLine,
  formatDocDate,
  formatYen,
  includingTax,
  isLineComplete,
  isLinePartial,
  isSameAddress,
  isoFromDate,
  lineAmounts,
  newLineKey,
  parseDiscountInput,
  parseNumberInput,
  sanitizeAddressList,
  todayIso,
  type AddressEntry,
  type AddressKind,
  type EditorLine,
  type EditorState,
} from './quote-editor/model';
import { ProductPanel } from './quote-editor/ProductPanel';
import { SidePanel } from './quote-editor/SidePanel';
import { TermsPanel } from './quote-editor/TermsPanel';

import { CategoryLogoHeader } from '@/components/common/CategoryLogoHeader';
import { Button } from '@/components/ui/button';
import { DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useDeal } from '@/hooks/useDeals';
import { useMailContact, useUpdateMailContact } from '@/hooks/useMailContacts';
import { useMailCompany } from '@/hooks/useMailSettings';
import {
  useActiveCatalogs,
  useAllActiveCatalogItems,
  useCatalogItems,
  type CatalogItemRecord,
  type ProductCatalogRecord,
} from '@/hooks/useProductCatalogs';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useCreateQuote, useUpdateQuoteDates, useUpdateQuoteDraft } from '@/hooks/useQuotes';
import { useGeneralSettings, useSalesSettingsWithDefaults } from '@/hooks/useSettings';
import {
  DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE,
  canEditDocumentDates,
} from '@/lib/documentDatePermission';
import { toDate } from '@/lib/firestoreTime';

/**
 * 「セール期間中のモジュール割引忘れ」警告の発火条件 (従来どおり)。
 *  ① 営業設定の季節モードが 'sale'  ② 商談の主軸カテゴリー名が 'TimeWaver'  ③ 見積行の製品カテゴリが 'Module'
 */
const SALE_WARNING_PRIMARY_CATEGORY_NAME = 'TimeWaver';
const SALE_WARNING_PRODUCT_CATEGORY = 'Module';
const SALE_MODULE_DISCOUNT_WARNING = '⚠️セール期間中のモジュールの割引設定を忘れずに！';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  /** 商談に紐づく contactId（住所の一覧を引くのに必須） */
  contactId: string;
  defaultRecipientEmail?: string | null;
  defaultTitle?: string;
  /**
   * 開いたときに自動投入する商品リスト（個別説明記録の検討グループから渡す等）。
   * productId が全アクティブカタログ items のいずれかと一致すれば価格込みで投入。
   * 一致しない場合は製品リストにない品目 (単価は未入力) として投入する。
   */
  initialItems?: ConsideredProductItem[];
  /** 既存の下書き見積書を編集する場合に渡す */
  editingQuote?: { id: string; data: Quote } | null;
  /** 「メール送信」で呼ばれる。親側でこの quoteId のメール送信画面を開く */
  onCreatedForSend?: (quoteId: string) => void;
}

export function CreateQuoteDialog(props: Props) {
  const { open, onOpenChange } = props;
  /** エディタが閉じる前に入力中の欄を確定する処理 (エディタ側が差し込む) */
  const beforeCloseRef = useRef<() => void>(() => {});
  /** Esc で先にパネル・カレンダーを閉じる処理 (閉じたら true) */
  const escapeRef = useRef<() => boolean>(() => false);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) beforeCloseRef.current();
        onOpenChange(o);
      }}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[260] flex flex-col bg-[var(--bg-root)] focus:outline-none"
          onEscapeKeyDown={(e) => {
            if (escapeRef.current()) e.preventDefault();
          }}
        >
          {open && (
            <EditorLoader {...props} beforeCloseRef={beforeCloseRef} escapeRef={escapeRef} />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

type EditorProps = Props & {
  beforeCloseRef: MutableRefObject<() => void>;
  escapeRef: MutableRefObject<() => boolean>;
};

/** 初期値の計算に必要なデータ (個別説明からの商品投入ならカタログ) を待ってからエディタを出す。 */
function EditorLoader(props: EditorProps) {
  const allCatalogItems = useAllActiveCatalogItems();
  const needsCatalog = !props.editingQuote && (props.initialItems?.length ?? 0) > 0;
  const ready = !needsCatalog || !allCatalogItems.isLoading;
  const [initial, setInitial] = useState<EditorState | null>(null);

  useEffect(() => {
    if (!ready || initial) return;
    setInitial(buildInitialState(props, allCatalogItems.data ?? []));
  }, [ready, initial, props, allCatalogItems.data]);

  if (!initial) {
    return (
      <div className="flex-1 grid place-items-center">
        <DialogPrimitive.Title className="sr-only">見積書</DialogPrimitive.Title>
        <Spinner />
      </div>
    );
  }
  return <QuoteEditor {...props} initial={initial} />;
}

function buildInitialState(
  props: Props,
  allItems: Array<CatalogItemRecord & { catalogId?: string }>,
): EditorState {
  const q = props.editingQuote?.data;
  if (q) {
    const issued = toDate(q.issuedAt);
    const valid = toDate(q.validUntil);
    return {
      title: q.title ?? '',
      issued: issued ? isoFromDate(issued) : '',
      valid: valid ? isoFromDate(valid) : '',
      lines: (q.lineItems ?? []).map((it) => ({
        key: newLineKey(),
        // 既存行は SKU があれば製品リストの品目 (単価は固定)
        fromCatalog: !!it.sku,
        catalogId: null,
        itemId: null,
        name: it.name,
        modelName: it.modelName ?? '',
        variation: it.variation ?? null,
        sku: it.sku ?? null,
        productCategory: null,
        priceExcludingTax: it.priceExcludingTax,
        priceIncludingTax: it.priceIncludingTax,
        quantity: it.quantity,
        discountType: it.discountType ?? 'amount',
        discountValue:
          it.discountValue != null && it.discountValue > 0 ? String(it.discountValue) : '',
        discountOpen: false,
      })),
      paymentMethod: q.paymentMethod,
      installments: q.installments ?? 12,
      hasTradeIn: q.hasTradeIn,
      tradeInType: q.tradeInType ?? 'discount',
      tradeInAmount: q.tradeInAmount ?? null,
      remarks: q.remarks ?? '',
      hideDiscounts: q.hideDiscounts === true,
      hideModelNames: q.hideModelNames === true,
      billingId: q.billingAddressSnapshot?.id ?? null,
      deliveryId:
        q.deliveryAddressSnapshot === null
          ? DELIVERY_SAME_AS_BILLING
          : (q.deliveryAddressSnapshot?.id ?? null),
      billingTouched: false,
      deliveryTouched: false,
    };
  }

  const lines: EditorLine[] = (props.initialItems ?? []).map((it) => {
    const match = it.productId ? allItems.find((m) => m.id === it.productId) : undefined;
    // 個別説明の検討商品で入力された割引を見積行に引き継ぐ
    const discountType = it.discountType ?? 'amount';
    const discountValue =
      it.discountValue != null && it.discountValue > 0 ? String(it.discountValue) : '';
    if (match) {
      return {
        ...emptyLine(),
        fromCatalog: true,
        catalogId: match.catalogId ?? null,
        itemId: match.id,
        name: match.name,
        modelName: match.modelName,
        variation: match.variation ?? null,
        sku: match.sku,
        productCategory: match.productCategory ?? null,
        priceExcludingTax: match.priceExcludingTax,
        priceIncludingTax: match.priceIncludingTax,
        quantity: it.quantity,
        discountType,
        discountValue,
      };
    }
    // 製品リストに無い商品は、単価を未入力 (点線の枠) にして入力してもらう
    return {
      ...emptyLine(),
      name: it.productName,
      quantity: it.quantity,
      discountType,
      discountValue,
    };
  });

  return {
    title: props.defaultTitle ?? '',
    issued: '',
    valid: '',
    lines: lines.length > 0 ? lines : [emptyLine()],
    paymentMethod: 'transfer',
    installments: 12,
    hasTradeIn: false,
    tradeInType: 'discount',
    tradeInAmount: null,
    remarks: '',
    // 新しい見積書は「割引金額を表示」「製品型番の表示」ともオフで始める (2026-10-16)
    hideDiscounts: true,
    hideModelNames: true,
    billingId: null,
    deliveryId: null,
    billingTouched: true,
    deliveryTouched: true,
  };
}

type PanelState =
  | { kind: 'product'; lineKey: string }
  | { kind: 'billing' }
  | { kind: 'delivery' }
  | { kind: 'terms' }
  | null;

type SaveStatus =
  | { kind: 'saved-before' }
  | { kind: 'unsaved' }
  | { kind: 'needs-line' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: Date; created: boolean; partial: boolean }
  | { kind: 'error'; message: string };

/** 書類 (白い紙) の上の入力欄。紙は印刷物なのでテーマに関係なく白地・黒字。 */
const paperField =
  'w-full min-w-0 bg-transparent text-inherit rounded-[3px] px-1 -mx-1 py-0.5 -my-0.5 outline outline-[1.5px] outline-transparent ' +
  'hover:outline-[#9bbbe0] focus:outline-2 focus:outline-[#2b62a0] focus:bg-white ' +
  'placeholder:text-[#a35f0e] placeholder-shown:outline-dashed placeholder-shown:outline-[#d97706] placeholder-shown:bg-[#fff8ec] ' +
  'focus:placeholder-shown:outline-solid focus:placeholder-shown:outline-[#2b62a0] focus:placeholder-shown:bg-white';

function zoneClass(empty: boolean, active: boolean) {
  return [
    'block rounded px-1.5 py-1 -mx-1.5 -my-1 cursor-pointer outline outline-[1.5px] outline-offset-0',
    empty
      ? 'outline-dashed outline-[#d97706] bg-[#fff8ec]'
      : 'outline-transparent hover:outline-[#9bbbe0] hover:bg-[rgba(43,98,160,0.035)]',
    active ? '!outline-2 !outline-solid !outline-[#2b62a0]' : '',
    'focus-visible:outline-2 focus-visible:outline-[#2b62a0]',
  ].join(' ');
}

function blurActiveElement() {
  const el = document.activeElement as HTMLElement | null;
  if (el && el !== document.body && typeof el.blur === 'function') el.blur();
}

function QuoteEditor(props: EditorProps & { initial: EditorState }) {
  const {
    onOpenChange,
    dealId,
    contactId,
    editingQuote,
    onCreatedForSend,
    initial,
    beforeCloseRef,
    escapeRef,
  } = props;
  const isEditMode = !!editingQuote;
  const { user } = useAuth();

  // ─── 参照データ ───
  const dealQuery = useDeal(dealId);
  const deal = dealQuery.data ?? null;
  const categoriesQuery = useProductCategories({ includeInactive: true });
  const primaryCategory = useMemo(() => {
    const pid = deal?.primaryCategoryId;
    if (!pid) return null;
    return categoriesQuery.data?.find((c) => c.id === pid) ?? null;
  }, [deal?.primaryCategoryId, categoriesQuery.data]);
  const { merged: salesSettings } = useSalesSettingsWithDefaults();
  const general = useGeneralSettings();
  const mailCompany = useMailCompany();
  const contactQuery = useMailContact(contactId);
  const contact = contactQuery.data ?? null;

  const activeCatalogs = useActiveCatalogs();
  const allCatalogItems = useAllActiveCatalogItems();
  const [showAllCatalogs, setShowAllCatalogs] = useState(false);
  const visibleCatalogs = useMemo<ProductCatalogRecord[]>(() => {
    const all = activeCatalogs.data ?? [];
    if (showAllCatalogs) return all;
    const brands = primaryCategory?.linkedBrandNames ?? [];
    if (brands.length === 0) return all;
    const set = new Set(brands);
    const filtered = all.filter((c) => set.has(c.category));
    return filtered.length > 0 ? filtered : all;
  }, [activeCatalogs.data, showAllCatalogs, primaryCategory]);
  const isFiltered =
    !showAllCatalogs &&
    (primaryCategory?.linkedBrandNames?.length ?? 0) > 0 &&
    visibleCatalogs.length < (activeCatalogs.data?.length ?? 0);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);
  useEffect(() => {
    if (visibleCatalogs.length === 0) return;
    if (selectedCatalogId && visibleCatalogs.some((c) => c.id === selectedCatalogId)) return;
    setSelectedCatalogId(visibleCatalogs[0].id);
  }, [visibleCatalogs, selectedCatalogId]);
  const catalogItems = useCatalogItems(selectedCatalogId);

  /** SKU → 製品カテゴリ。既存行は製品カテゴリを保存していないため、表示時にカタログから引き直す */
  const productCategoryBySku = useMemo(() => {
    const map = new Map<string, string>();
    for (const it of allCatalogItems.data ?? []) {
      if (it.sku && it.productCategory) map.set(it.sku, it.productCategory);
    }
    return map;
  }, [allCatalogItems.data]);
  const showsModuleDiscountWarning =
    salesSettings.seasonMode === 'sale' &&
    primaryCategory?.name === SALE_WARNING_PRIMARY_CATEGORY_NAME;

  // ─── 状態 ───
  const stateRef = useRef<EditorState>(initial);
  const [state, setState] = useState<EditorState>(initial);
  const apply = useCallback((fn: (s: EditorState) => EditorState) => {
    const next = fn(stateRef.current);
    stateRef.current = next;
    setState(next);
    return next;
  }, []);

  const quoteIdRef = useRef<string | null>(editingQuote?.id ?? null);
  const [quoteId, setQuoteId] = useState<string | null>(editingQuote?.id ?? null);
  const [saveStatus, setSaveStatusRaw] = useState<SaveStatus>(
    isEditMode ? { kind: 'saved-before' } : { kind: 'unsaved' },
  );
  const saveStatusRef = useRef(saveStatus);
  const setSaveStatus = (s: SaveStatus) => {
    saveStatusRef.current = s;
    setSaveStatusRaw(s);
  };
  const [panel, setPanel] = useState<PanelState>(null);
  const [datePopover, setDatePopover] = useState<'issued' | 'valid' | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [focusRequest, setFocusRequest] = useState<string | null>(null);
  const [localLists, setLocalLists] = useState<{
    billing?: BillingAddress[];
    delivery?: DeliveryAddress[];
  }>({});
  const panelRef = useRef<HTMLDivElement>(null);
  /** 製品リストから選んだときは、品目欄の入力文字を「リストにない品目」として確定させない */
  const pickingRef = useRef(false);
  /** 何か入力を確定したか (閉じるときの案内に使う) */
  const interactedRef = useRef(false);

  // ─── 保存 (欄を離れるたび) ───
  const create = useCreateQuote();
  const updateDraft = useUpdateQuoteDraft();
  const updateDates = useUpdateQuoteDates();
  const updateContact = useUpdateMailContact();
  const mutationsRef = useRef({ create, updateDraft, updateDates });
  mutationsRef.current = { create, updateDraft, updateDates };
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const pendingRef = useRef<Promise<void> | null>(null);
  const datePendingRef = useRef<Promise<unknown> | null>(null);
  const validDaysRef = useRef(salesSettings.quoteValidDays);
  validDaysRef.current = salesSettings.quoteValidDays;

  const runSave = useCallback(async () => {
    savingRef.current = true;
    try {
      while (dirtyRef.current) {
        dirtyRef.current = false;
        const s = stateRef.current;
        const lineItems = s.lines.filter(isLineComplete).map((l) => {
          const discountValue = parseDiscountInput(l.discountValue);
          return {
            name: l.name.trim(),
            modelName: l.modelName,
            variation: l.variation ?? null,
            sku: l.sku ?? null,
            priceExcludingTax: l.priceExcludingTax as number,
            priceIncludingTax: l.priceIncludingTax ?? includingTax(l.priceExcludingTax as number),
            quantity: l.quantity as number,
            discountType: discountValue != null ? l.discountType : null,
            discountValue,
          };
        });
        if (lineItems.length === 0) {
          // createQuote / updateQuoteDraft は品目 1 件以上が必須
          setSaveStatus({ kind: 'needs-line' });
          continue;
        }
        const partial = s.lines.some(isLinePartial);
        const common = {
          dealId,
          title: s.title.trim() || undefined,
          lineItems,
          paymentMethod: s.paymentMethod,
          installments: s.paymentMethod === 'credit_card' ? s.installments : null,
          hasTradeIn: s.hasTradeIn,
          tradeInType: s.hasTradeIn ? s.tradeInType : null,
          tradeInAmount: s.hasTradeIn ? (s.tradeInAmount ?? 0) : null,
          remarks: s.remarks.trim() || null,
          hideDiscounts: s.hideDiscounts,
          hideModelNames: s.hideModelNames,
        };
        setSaveStatus({ kind: 'saving' });
        const m = mutationsRef.current;
        if (!quoteIdRef.current) {
          const r = await m.create.mutateAsync({
            ...common,
            // 常に下書きとして作成し、送信はメール送信画面から行う
            sendMail: false,
            recipientEmail: null,
            issuedAtOverride: s.issued || null,
            validUntilOverride: s.valid || null,
            billingAddressId: s.billingId,
            deliveryAddressId: s.deliveryId,
          });
          quoteIdRef.current = r.quoteId;
          setQuoteId(r.quoteId);
          // サーバーが決めた日付 (未入力なら 本日 / 本日 + 営業設定の日数) を画面にも出す
          apply((st) => {
            const issued = st.issued || todayIso();
            return { ...st, issued, valid: st.valid || addDaysIso(issued, validDaysRef.current) };
          });
          setSaveStatus({ kind: 'saved', at: new Date(), created: true, partial });
        } else {
          await m.updateDraft.mutateAsync({
            ...common,
            quoteId: quoteIdRef.current,
            // 下書きの編集では、住所を選び直すまで保存済みの住所をそのまま使う。
            // Callable は undefined の値を null に変えて送り、サーバーが「顧客の既定」に解決し直すため、
            // 選び直していない住所はキーごと送らない。
            ...(s.billingTouched ? { billingAddressId: s.billingId } : {}),
            ...(s.deliveryTouched ? { deliveryAddressId: s.deliveryId } : {}),
          });
          setSaveStatus({ kind: 'saved', at: new Date(), created: false, partial });
        }
      }
    } catch (err) {
      const message = (err as Error).message;
      setSaveStatus({ kind: 'error', message });
      toast.error(`下書きの保存に失敗しました: ${message}`);
    } finally {
      savingRef.current = false;
    }
  }, [apply, dealId]);

  const requestSave = useCallback(() => {
    interactedRef.current = true;
    dirtyRef.current = true;
    if (!savingRef.current) pendingRef.current = runSave();
  }, [runSave]);

  /** 状態を変えて下書きを保存する */
  const change = useCallback(
    (fn: (s: EditorState) => EditorState) => {
      apply(fn);
      requestSave();
    },
    [apply, requestSave],
  );
  const updateLine = (key: string, fn: (l: EditorLine) => EditorLine) =>
    change((s) => ({ ...s, lines: s.lines.map((l) => (l.key === key ? fn(l) : l)) }));

  const waitForSaves = async () => {
    while (savingRef.current || dirtyRef.current) {
      if (!savingRef.current && dirtyRef.current) pendingRef.current = runSave();
      await pendingRef.current;
    }
    await datePendingRef.current;
  };

  // ─── 日付 ───
  const createdByMe = !isEditMode && !!quoteId;
  const canEditDates =
    !quoteId ||
    createdByMe ||
    canEditDocumentDates(
      user,
      {
        createdBy: deal?.createdBy ?? '',
        assigneeUid: deal?.assigneeUid ?? null,
        briefingAssigneeUid: deal?.briefingAssigneeUid ?? null,
        salesAgentUid: deal?.salesAgentUid ?? null,
      },
      { createdBy: editingQuote?.data.createdBy ?? null },
      'quote',
    );

  const commitDate = (k: 'issued' | 'valid', iso: string) => {
    apply((s) => ({ ...s, [k]: iso }));
    interactedRef.current = true;
    const qid = quoteIdRef.current;
    if (!qid) {
      // まだ下書きが無い: 作成時に一緒に送る
      requestSave();
      return;
    }
    setSaveStatus({ kind: 'saving' });
    const p = mutationsRef.current.updateDates
      .mutateAsync({
        dealId,
        quoteId: qid,
        ...(k === 'issued' ? { issuedAt: new Date(iso) } : { validUntil: new Date(iso) }),
      })
      .then(() => {
        setSaveStatus({
          kind: 'saved',
          at: new Date(),
          created: false,
          partial: stateRef.current.lines.some(isLinePartial),
        });
      })
      .catch((err: { code?: string; message?: string }) => {
        const message =
          err.code === 'permission-denied'
            ? DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE
            : (err.message ?? '');
        setSaveStatus({ kind: 'error', message });
        toast.error(`日付の保存に失敗しました: ${message}`);
      });
    datePendingRef.current = p;
  };

  // ─── 住所 ───
  const canEditContact = user?.role === 'owner' || user?.role === 'editor';
  const billingList: BillingAddress[] =
    localLists.billing ?? (contact ? getBillingAddresses(contact) : []);
  const deliveryList: DeliveryAddress[] = localLists.delivery ?? contact?.deliveryAddresses ?? [];
  const defaultBilling = billingList.find((a) => a.isDefault) ?? billingList[0] ?? null;
  const defaultDelivery = deliveryList.find((a) => a.isDefault) ?? deliveryList[0] ?? null;
  const savedQuote = editingQuote?.data;

  const billingShown: AddressEntry | null =
    isEditMode && !state.billingTouched
      ? (savedQuote?.billingAddressSnapshot ?? defaultBilling)
      : (billingList.find((a) => a.id === state.billingId) ?? defaultBilling);
  const deliveryShown: AddressEntry | null =
    isEditMode && !state.deliveryTouched
      ? savedQuote?.deliveryAddressSnapshot === undefined
        ? defaultDelivery
        : savedQuote.deliveryAddressSnapshot
      : state.deliveryId === DELIVERY_SAME_AS_BILLING
        ? null
        : (deliveryList.find((a) => a.id === state.deliveryId) ?? defaultDelivery);
  const deliveryVisibleOnDoc = !!deliveryShown && !isSameAddress(billingShown, deliveryShown);

  /** パネルで選ばれている選択肢と、一覧に無い保存済みの住所 */
  const addressSelection = (
    kind: AddressKind,
  ): { value: string | null; savedElsewhere: string | null } => {
    const list = kind === 'billing' ? billingList : deliveryList;
    const shown = kind === 'billing' ? billingShown : deliveryShown;
    const touched = kind === 'billing' ? state.billingTouched : state.deliveryTouched;
    if (kind === 'delivery' && !shown)
      return { value: DELIVERY_SAME_AS_BILLING, savedElsewhere: null };
    if (!shown) return { value: null, savedElsewhere: null };
    if (list.some((a) => a.id === shown.id)) return { value: shown.id, savedElsewhere: null };
    return {
      value: null,
      savedElsewhere: isEditMode && !touched ? addressSummary(shown) || '（内容なし）' : null,
    };
  };

  const selectAddress = (kind: AddressKind, value: string) =>
    change((s) =>
      kind === 'billing'
        ? { ...s, billingId: value, billingTouched: true }
        : { ...s, deliveryId: value, deliveryTouched: true },
    );

  const saveAddress = async (kind: AddressKind, entry: AddressEntry) => {
    const base: AddressEntry[] = kind === 'billing' ? billingList : deliveryList;
    let next = base.some((a) => a.id === entry.id)
      ? base.map((a) => (a.id === entry.id ? entry : a))
      : [...base, entry];
    if (entry.isDefault) next = next.map((a) => ({ ...a, isDefault: a.id === entry.id }));
    const clean = sanitizeAddressList(next);
    setLocalLists((prev) => ({ ...prev, [kind]: clean }));
    try {
      await updateContact.mutateAsync({
        id: contactId,
        data:
          kind === 'billing'
            ? { billingAddresses: clean, ...buildLegacyAddressFields(clean) }
            : { deliveryAddresses: clean },
      });
    } catch (err) {
      toast.error(`顧客情報への住所の保存に失敗しました: ${(err as Error).message}`);
      return;
    }
    // 顧客情報への保存が済んでから、この見積書の宛先にして下書きを保存する (サーバーが顧客情報から住所を読むため)
    if (clean.some((a) => a.id === entry.id)) selectAddress(kind, entry.id);
  };

  // ─── 品目 ───
  const openProductPanel = (lineKey: string, name: string) => {
    setDatePopover(null);
    setProductQuery(name);
    setPanel({ kind: 'product', lineKey });
  };
  const scheduleProductPanelClose = () => {
    setTimeout(() => {
      const el = document.activeElement as HTMLElement | null;
      if (!el?.closest('[data-line-name]')) {
        setPanel((p) => (p?.kind === 'product' ? null : p));
      }
    }, 0);
  };

  const withCatalogItem = (
    l: EditorLine,
    item: CatalogItemRecord & { catalogId?: string },
  ): EditorLine => ({
    ...l,
    fromCatalog: true,
    catalogId: item.catalogId ?? selectedCatalogId,
    itemId: item.id,
    name: item.name,
    modelName: item.modelName,
    variation: item.variation ?? null,
    sku: item.sku,
    productCategory: item.productCategory ?? null,
    priceExcludingTax: item.priceExcludingTax,
    priceIncludingTax: item.priceIncludingTax,
    quantity: 1,
  });

  const pickProduct = (item: CatalogItemRecord) => {
    if (panel?.kind !== 'product') return;
    const key = panel.lineKey;
    pickingRef.current = true;
    blurActiveElement();
    pickingRef.current = false;
    updateLine(key, (l) => withCatalogItem(l, item));
    setPanel(null);
  };

  const commitLineName = (key: string, text: string) => {
    if (pickingRef.current) return;
    const line = stateRef.current.lines.find((l) => l.key === key);
    if (!line) return;
    const t = text.trim();
    if (t === line.name) return;
    const match =
      (catalogItems.data ?? []).find((it) => it.name === t) ??
      (allCatalogItems.data ?? []).find((it) => it.name === t);
    if (match) {
      updateLine(key, (l) => withCatalogItem(l, match));
      return;
    }
    // 製品リストにない品目: 数量・単価は手入力、税込は単価から計算
    updateLine(key, (l) => ({
      ...l,
      name: t,
      fromCatalog: false,
      catalogId: null,
      itemId: null,
      modelName: '',
      variation: null,
      sku: null,
      productCategory: null,
      priceIncludingTax: l.priceExcludingTax != null ? includingTax(l.priceExcludingTax) : null,
    }));
  };

  const commitQuantity = (key: string, text: string) => {
    const line = stateRef.current.lines.find((l) => l.key === key);
    if (!line) return;
    const n = parseNumberInput(text);
    const next =
      text.trim() === '' ? null : n !== null ? Math.max(1, Math.floor(n)) : line.quantity;
    if (next === line.quantity) return;
    updateLine(key, (l) => ({ ...l, quantity: next }));
  };

  const commitPrice = (key: string, text: string) => {
    const line = stateRef.current.lines.find((l) => l.key === key);
    if (!line) return;
    const n = parseNumberInput(text);
    const next =
      text.trim() === '' ? null : n !== null && n >= 0 ? Math.round(n) : line.priceExcludingTax;
    if (next === line.priceExcludingTax) return;
    updateLine(key, (l) => ({
      ...l,
      priceExcludingTax: next,
      priceIncludingTax: next !== null ? includingTax(next) : null,
    }));
  };

  const commitDiscount = (key: string, text: string) => {
    const line = stateRef.current.lines.find((l) => l.key === key);
    if (!line) return;
    const n = parseDiscountInput(text);
    if (n !== null && line.discountType === 'percent' && n > 100) {
      toast.error('割引率は100%以下で入力してください');
      return;
    }
    const value = n !== null ? String(n) : '';
    if (value === line.discountValue) {
      if (!value && line.discountOpen)
        apply((s) => ({
          ...s,
          lines: s.lines.map((l) => (l.key === key ? { ...l, discountOpen: false } : l)),
        }));
      return;
    }
    updateLine(key, (l) => ({ ...l, discountValue: value, discountOpen: value !== '' }));
  };

  const addLine = () => {
    const line = emptyLine();
    apply((s) => ({ ...s, lines: [...s.lines, line] }));
    setFocusRequest(`[data-line-name="${line.key}"]`);
  };
  const removeLine = (key: string) => {
    if (panel?.kind === 'product' && panel.lineKey === key) setPanel(null);
    change((s) => ({ ...s, lines: s.lines.filter((l) => l.key !== key) }));
  };
  const openDiscount = (key: string) => {
    apply((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.key === key ? { ...l, discountOpen: true } : l)),
    }));
    setFocusRequest(`[data-line-discount="${key}"]`);
  };

  useEffect(() => {
    if (!focusRequest) return;
    document.querySelector<HTMLElement>(focusRequest)?.focus();
    setFocusRequest(null);
  }, [focusRequest]);

  // ─── パネルの開閉 ───
  const panelKindRef = useRef<string | null>(null);
  panelKindRef.current = panel?.kind ?? null;
  const closePanel = useCallback(() => {
    const active = document.activeElement as HTMLElement | null;
    // 入力中の欄を確定してから閉じる (品目のパネルは品目欄に焦点がある)
    if (
      panelRef.current?.contains(active) ||
      (panelKindRef.current === 'product' && active?.closest('[data-line-name]'))
    ) {
      blurActiveElement();
    }
    setPanel(null);
  }, []);
  const openZone = (kind: 'billing' | 'delivery' | 'terms') => {
    setDatePopover(null);
    setPanel({ kind });
  };

  // パネルの外をクリックしたら閉じる (品目のパネルは品目欄から離れたときに閉じる)
  const onRootMouseDown = (e: MouseEvent) => {
    if (!panel || panel.kind === 'product') return;
    const target = e.target as HTMLElement;
    if (panelRef.current?.contains(target) || target.closest('[data-panel-trigger]')) return;
    closePanel();
  };

  // Esc はまずカレンダー・パネルを閉じる
  escapeRef.current = () => {
    if (datePopover) {
      setDatePopover(null);
      return true;
    }
    if (panel) {
      closePanel();
      return true;
    }
    return false;
  };

  const notifyUnsaved = () => {
    const s = stateRef.current;
    if (!quoteIdRef.current && interactedRef.current) {
      toast.message('品目がそろっていないため、見積書は作成していません');
    } else if (s.lines.some(isLinePartial)) {
      toast.message('品目名・数量・単価がそろっていない行は保存されていません');
    }
  };
  beforeCloseRef.current = () => {
    blurActiveElement();
    notifyUnsaved();
  };

  const handleClose = () => {
    blurActiveElement();
    notifyUnsaved();
    onOpenChange(false);
  };

  const handleSend = async () => {
    blurActiveElement();
    setPanel(null);
    setDatePopover(null);
    await waitForSaves();
    const s = stateRef.current;
    const partial = s.lines.filter(isLinePartial);
    if (partial.length > 0) {
      toast.error(
        `品目名・数量・単価がそろっていない行があります: ${partial.map((l) => l.name || '（品目名なし）').join(' / ')}`,
      );
      return;
    }
    const complete = s.lines.filter(isLineComplete);
    if (complete.length === 0) {
      toast.error('見積行を1件以上追加してください');
      return;
    }
    if (saveStatusRef.current.kind === 'error') {
      toast.error('下書きを保存できていないため、メール送信画面を開けません');
      return;
    }
    // 「値引き額に商品価格を入れてしまった」誤入力の最終ガード (従来どおり)
    const clamped = complete.filter((l) => lineAmounts(l).discountClamped);
    if (clamped.length > 0) {
      const ok = confirm(
        `次の行は値引き額が小計を超えているため 0 円になります: ${clamped.map((l) => l.name).join(' / ')}\n\n` +
          '値引き欄には「商品価格」ではなく「値引き分の金額」を入力してください。\n' +
          'このまま進みますか？',
      );
      if (!ok) return;
    } else {
      const t = computeTotals(s);
      if (t.discountTotal > 0 && t.totalIncl === 0) {
        const ok = confirm(
          '値引きが入力されているため、合計金額が ¥0 の見積書になります。\n' +
            '値引き欄に商品価格を入力していないかご確認ください。\n\nこのまま進みますか？',
        );
        if (!ok) return;
      }
    }
    const qid = quoteIdRef.current;
    if (!qid) return;
    onOpenChange(false);
    onCreatedForSend?.(qid);
  };

  // ─── 表示用の値 ───
  const totals = computeTotals(state);
  const zeroTotalWithDiscount = totals.discountTotal > 0 && totals.totalIncl === 0;
  const contactKind = deal?.contactKind ?? 'btoc';
  const deliveryHeading = contactKind === 'btob' ? 'ご納品先（予定）' : 'お届け先（予定）';
  const vendor = useMemo(() => {
    // 公開見積 (resolveCompanyInfo) と同じ順: カテゴリー別 → 全般設定 → 旧会社情報 → 会社名
    const pick = (
      ci:
        | Partial<
            Record<
              'name' | 'representative' | 'postalCode' | 'address' | 'phone' | 'email',
              string | null
            >
          >
        | null
        | undefined,
    ) =>
      ci && (ci.name || ci.representative || ci.postalCode || ci.address || ci.phone || ci.email)
        ? ci
        : null;
    const ci =
      pick(primaryCategory?.companyInfo) ??
      pick(general.data?.companyInfo) ??
      pick(mailCompany.data as Partial<Record<string, string>> | null | undefined);
    return {
      name: ci?.name || general.data?.companyName || 'caudesign',
      postalCode: ci?.postalCode || '',
      address: ci?.address || '',
      phone: ci?.phone || '',
      email: ci?.email || '',
      representative: ci?.representative || '',
    };
  }, [primaryCategory, general.data, mailCompany.data]);
  const logo = primaryCategory?.logoStoragePath
    ? {
        url: primaryCategory.logoStoragePath,
        position: primaryCategory.logoPosition ?? CATEGORY_LOGO_POSITION_DEFAULT,
      }
    : null;
  const productLine =
    panel?.kind === 'product' ? state.lines.find((l) => l.key === panel.lineKey) : null;

  const statusText = (() => {
    switch (saveStatus.kind) {
      case 'saved-before':
        return '下書き（保存済み）';
      case 'unsaved':
        return '未保存（品目が 1 件そろうと下書きを作成し、以降は欄を離れるたびに保存します）';
      case 'needs-line':
        return '未保存（品目名・数量・単価がそろった品目が 1 件以上必要です）';
      case 'saving':
        return '保存中…';
      case 'saved': {
        const hm = `${saveStatus.at.getHours()}:${String(saveStatus.at.getMinutes()).padStart(2, '0')}`;
        return `${saveStatus.created ? '下書きを作成しました' : '下書きを保存しました'} ${hm}${
          saveStatus.partial ? '（品目名・数量・単価がそろっていない行は保存されません）' : ''
        }`;
      }
      case 'error':
        return `保存に失敗しました: ${saveStatus.message}`;
    }
  })();
  const statusDot =
    saveStatus.kind === 'saving'
      ? 'bg-[var(--accent-amber)]'
      : saveStatus.kind === 'error'
        ? 'bg-[var(--accent-red)]'
        : saveStatus.kind === 'saved' || saveStatus.kind === 'saved-before'
          ? 'bg-[var(--accent-green)]'
          : 'bg-[var(--text-muted)]';

  const zoneKeyDown = (e: KeyboardEvent, kind: 'billing' | 'delivery' | 'terms') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openZone(kind);
    }
  };

  const dateField = (k: 'issued' | 'valid') => {
    const v = state[k];
    if (!canEditDates) {
      return (
        <span
          className="inline-flex items-center gap-1 cursor-not-allowed"
          title={DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE}
          aria-label={DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE}
        >
          {formatDocDate(v)}
          <Lock className="size-3 text-gray-500" />
        </span>
      );
    }
    const placeholder =
      k === 'issued' ? '未入力（本日の日付になります）' : '未入力（営業設定の日数で自動計算）';
    return (
      <span className="relative inline-block">
        <span
          role="button"
          tabIndex={0}
          data-panel-trigger
          aria-label={`${k === 'issued' ? '発行日' : '有効期限'}を変更`}
          onClick={() => {
            if (datePopover === k) return;
            closePanel();
            setDatePopover(k);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              closePanel();
              setDatePopover(k);
            }
          }}
          className={`inline-block ${zoneClass(!v, datePopover === k)} !py-0.5 !-my-0.5 !mx-0`}
        >
          {v ? formatDocDate(v) : <span className="text-[#a35f0e]">{placeholder}</span>}
        </span>
        {datePopover === k && (
          <DatePickerPopover
            label={k === 'issued' ? '発行日' : '有効期限'}
            value={v}
            fallbackIso={k === 'valid' && state.issued ? state.issued : todayIso()}
            onCommit={(iso) => commitDate(k, iso)}
            onClose={() => setDatePopover(null)}
          />
        )}
      </span>
    );
  };

  return (
    <div
      className="relative flex-1 flex flex-col min-h-0 overflow-hidden"
      onMouseDown={onRootMouseDown}
    >
      {/* 上部バー */}
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-surface)]">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
          <DialogPrimitive.Title className="text-[15px] font-bold text-[var(--text-primary)] whitespace-nowrap">
            {isEditMode ? '見積書を編集（下書き）' : '見積書を作成'}
          </DialogPrimitive.Title>
          <span
            className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)]"
            aria-live="polite"
          >
            <span className={`size-2 rounded-full shrink-0 ${statusDot}`} />
            {statusText}
          </span>
        </div>
        <div className="flex-1" />
        {/* 保存する値は「非表示」(hideModelNames / hideDiscounts)。スイッチはその逆で、オン = 表示 */}
        <HeaderSwitch
          label="製品型番の表示"
          checked={!state.hideModelNames}
          title="オンにすると、見積書・請求書上で品目名の下に型番を表示します。オフのときは表示しません。"
          onToggle={() => change((s) => ({ ...s, hideModelNames: !s.hideModelNames }))}
        />
        <HeaderSwitch
          label="割引金額を表示"
          checked={!state.hideDiscounts}
          title="オンにすると、見積書・請求書上で行割引の内訳と「割引前小計 / 割引合計」を表示します。オフのときは表示せず、単価は割引後の実質単価で表示します（合計金額は変わりません）。"
          onToggle={() => change((s) => ({ ...s, hideDiscounts: !s.hideDiscounts }))}
        />
        <Button variant="ghost" size="sm" onClick={handleClose}>
          閉じる
        </Button>
        <Button variant="primary" size="sm" onClick={() => void handleSend()}>
          メール送信
        </Button>
      </header>
      <p className="px-4 py-1.5 text-[11px] text-[var(--text-muted)] border-b border-[var(--border)] bg-[var(--bg-surface)]">
        点線の枠は未入力の欄です。書類の上の文字・欄をクリックして編集します。Enter（日本語の変換を確定する
        Enter は除く）・欄の外のクリック・別の欄のクリックで確定し、下書きを保存します。
      </p>

      {/* 書類 + 右から出るパネル (パネルは上部バーの下に出し、「閉じる」「メール送信」を隠さない) */}
      <div className="relative flex-1 min-h-0 flex">
        <div
          className={`flex-1 min-w-0 overflow-y-auto px-4 py-7 transition-[padding] duration-200 motion-reduce:transition-none ${
            panel ? 'min-[1240px]:pr-[456px]' : ''
          }`}
        >
          <div className="max-w-[210mm] mx-auto bg-white text-black shadow-md p-12 max-sm:p-5">
            <CategoryLogoHeader logo={logo} />
            <header className="border-b-2 border-black pb-4 mb-6">
              <h2 className="text-2xl font-bold tracking-wide text-center">御 見 積 書</h2>
            </header>

            <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-8 max-sm:gap-4 mb-6">
              <div className="min-w-0 space-y-2">
                <p className="text-sm" title="顧客情報の氏名（この画面では変更できません）">
                  {contact?.companyName && (
                    <>
                      <span className="font-semibold">{contact.companyName}</span>
                      <br />
                    </>
                  )}
                  {contact?.department && (
                    <>
                      {contact.department}
                      <br />
                    </>
                  )}
                  <span className="text-base font-medium">{contact?.name ?? ''} 様</span>
                </p>
                {/* 請求先住所 */}
                <div
                  role="button"
                  tabIndex={0}
                  data-panel-trigger
                  aria-label="請求先住所を選ぶ・編集する"
                  onClick={() => openZone('billing')}
                  onKeyDown={(e) => zoneKeyDown(e, 'billing')}
                  className={`text-xs ${zoneClass(!billingShown && !contactQuery.isError, panel?.kind === 'billing')}`}
                >
                  {billingShown ? (
                    <p>
                      {billingShown.postalCode && (
                        <>
                          〒{billingShown.postalCode}
                          <br />
                        </>
                      )}
                      {addressLine(billingShown)}
                    </p>
                  ) : contactQuery.isError ? (
                    <span className="text-[#2b62a0]">
                      請求先住所: 顧客の既定の請求先住所を使います
                    </span>
                  ) : (
                    <span className="text-[#a35f0e]">請求先住所: 未入力</span>
                  )}
                </div>
                {/* 納品先住所 */}
                <div
                  role="button"
                  tabIndex={0}
                  data-panel-trigger
                  aria-label="納品先住所を選ぶ・編集する"
                  onClick={() => openZone('delivery')}
                  onKeyDown={(e) => zoneKeyDown(e, 'delivery')}
                  className={`text-xs ${zoneClass(false, panel?.kind === 'delivery')} ${
                    deliveryVisibleOnDoc ? '!mt-3 border-t border-gray-300 !pt-2' : ''
                  }`}
                >
                  {deliveryVisibleOnDoc && deliveryShown ? (
                    <>
                      <p className="font-semibold mb-0.5">
                        {deliveryHeading}
                        {deliveryShown.label ? ` (${deliveryShown.label})` : ''}
                      </p>
                      {deliveryShown.recipientName && <p>宛: {deliveryShown.recipientName}</p>}
                      {(deliveryShown.postalCode || addressLine(deliveryShown)) && (
                        <p>
                          {deliveryShown.postalCode ? `〒${deliveryShown.postalCode} ` : ''}
                          {addressLine(deliveryShown)}
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-[#2b62a0]">
                      {deliveryHeading}: 請求先住所と同じ（書類には表示されません）
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right max-sm:text-left text-sm space-y-0.5">
                <p>発行日: {dateField('issued')}</p>
                <p>有効期限: {dateField('valid')}</p>
                <p>
                  No.{' '}
                  {quoteId ? (
                    quoteId.slice(0, 12)
                  ) : (
                    <span className="text-xs text-[#2b62a0]">下書き作成後に表示</span>
                  )}
                </p>
              </div>
            </div>

            <div
              className="mb-6 text-right max-sm:text-left"
              title="設定の会社情報から表示します（この画面では変更できません）"
            >
              <p className="text-xs">{vendor.name}</p>
              {vendor.postalCode && <p className="text-xs">〒{vendor.postalCode}</p>}
              {vendor.address && <p className="text-xs">{vendor.address}</p>}
              {vendor.phone && <p className="text-xs">TEL: {vendor.phone}</p>}
              {vendor.email && <p className="text-xs">{vendor.email}</p>}
              {vendor.representative && <p className="text-xs mt-1">{vendor.representative}</p>}
            </div>

            <div className="flex items-baseline gap-1.5 text-base font-medium mb-2">
              <span className="whitespace-nowrap">件名:</span>
              <CommitInput
                value={state.title}
                aria-label="件名"
                placeholder="未入力（空欄なら件名を自動で付けます）"
                onCommit={(t) => {
                  const title = t.trim();
                  if (title !== stateRef.current.title) change((s) => ({ ...s, title }));
                }}
                className={`${paperField} font-normal`}
              />
            </div>
            <p className="text-sm mb-4">下記の通り、お見積もり申し上げます。</p>

            <div className="bg-gray-100 p-3 mb-4 inline-block">
              <span className="text-sm">合計金額 (税込)</span>
              <span className="ml-3 text-xl font-bold tabular-nums">
                {formatYen(totals.totalIncl)}
              </span>
            </div>

            <div className="max-sm:overflow-x-auto mb-2">
              <table className="w-full max-sm:min-w-[560px] text-sm border-collapse">
                <thead>
                  <tr className="border-y-2 border-black">
                    <th className="text-left p-2">品目</th>
                    <th className="text-right p-2 w-20">数量</th>
                    <th className="text-right p-2 w-28">単価 (税抜)</th>
                    <th className="text-right p-2 w-32">金額 (税込)</th>
                  </tr>
                </thead>
                <tbody>
                  {state.lines.map((l) => {
                    const complete = isLineComplete(l);
                    const a = complete ? lineAmounts(l) : null;
                    const hasDiscount = (a?.discountAmountExcludingTax ?? 0) > 0;
                    const discountShown =
                      l.discountOpen || parseDiscountInput(l.discountValue) !== null;
                    // 「製品型番の非表示」がオンなら型番を出さない (書類と同じ)
                    const modelLine = [
                      state.hideModelNames ? '' : l.modelName,
                      l.variation ? `(${l.variation})` : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const productCategory =
                      l.productCategory ??
                      (l.sku ? (productCategoryBySku.get(l.sku) ?? null) : null);
                    const needsModuleDiscountWarning =
                      showsModuleDiscountWarning &&
                      productCategory === SALE_WARNING_PRODUCT_CATEGORY &&
                      !hasDiscount;
                    return (
                      <tr key={l.key} className="group border-b border-gray-300">
                        <td className="p-2 align-middle">
                          <div className="flex items-center gap-1.5">
                            <CommitInput
                              data-line-name={l.key}
                              value={l.name}
                              aria-label="品目"
                              placeholder="品目名を入力 / リストから選択"
                              onFocus={(e) => openProductPanel(l.key, e.currentTarget.value)}
                              onTextChange={setProductQuery}
                              onCommit={(t) => commitLineName(l.key, t)}
                              onBlur={scheduleProductPanelClose}
                              className={paperField}
                            />
                            {!discountShown && (
                              <button
                                type="button"
                                onClick={() => openDiscount(l.key)}
                                className="shrink-0 px-1 py-0.5 rounded text-xs text-[#2b62a0] hover:bg-[#eef3fa] whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
                              >
                                ＋ 割引
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeLine(l.key)}
                              aria-label="この行を削除"
                              title="この行を削除"
                              className="shrink-0 p-1 rounded text-gray-500 hover:text-[#b42318] hover:bg-[#fdeeee] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </div>
                          {l.fromCatalog
                            ? modelLine && <div className="text-xs text-gray-500">{modelLine}</div>
                            : l.name && (
                                <div className="text-xs text-[#2b62a0]">
                                  製品リストにない品目（数量・単価を入力）
                                </div>
                              )}
                          {discountShown && (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-gray-700">
                              <span>割引</span>
                              <span
                                className="inline-flex rounded border border-[#cfd6de] overflow-hidden"
                                role="group"
                                aria-label="割引の種類"
                              >
                                {(['amount', 'percent'] as const).map((t) => (
                                  <button
                                    key={t}
                                    type="button"
                                    aria-pressed={l.discountType === t}
                                    // 値引き額の欄から焦点を外さない (空欄のまま離れると割引欄が閉じるため)
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      if (l.discountType !== t)
                                        updateLine(l.key, (x) => ({ ...x, discountType: t }));
                                    }}
                                    className={`px-2 leading-5 ${t === 'percent' ? 'border-l border-[#cfd6de]' : ''} ${
                                      l.discountType === t
                                        ? 'bg-[#2b62a0] text-white'
                                        : 'bg-white text-gray-700'
                                    }`}
                                  >
                                    {t === 'amount' ? '¥' : '%'}
                                  </button>
                                ))}
                              </span>
                              <CommitInput
                                data-line-discount={l.key}
                                value={l.discountValue}
                                aria-label="値引き額"
                                inputMode="decimal"
                                placeholder={l.discountType === 'percent' ? '例: 10' : '例: 50000'}
                                onCommit={(t) => commitDiscount(l.key, t)}
                                className={`${paperField} !w-[72px] !mx-0 text-right tabular-nums`}
                              />
                              {hasDiscount && a && (
                                <span className="tabular-nums">
                                  −{formatYen(a.discountAmountExcludingTax)}（税抜）
                                </span>
                              )}
                            </div>
                          )}
                          {state.hideDiscounts && hasDiscount && a && (
                            <div className="mt-0.5 text-xs text-[#2b62a0]">
                              割引は書類に表示しません（単価は割引後の{' '}
                              {formatYen(
                                Math.round(a.subtotalExcludingTax / Math.max(1, l.quantity ?? 1)),
                              )}{' '}
                              で表示）
                            </div>
                          )}
                          {a?.discountClamped && (
                            <div className="mt-1 text-xs text-[#b42318]">
                              値引き額が小計を超えているため、この行は 0
                              円になります。値引き欄には「商品価格」ではなく「値引き分の金額」を入力してください
                            </div>
                          )}
                          {needsModuleDiscountWarning && (
                            <div className="mt-1 text-xs text-[#a35f0e]">
                              {SALE_MODULE_DISCOUNT_WARNING}
                            </div>
                          )}
                        </td>
                        <td className="text-right p-2 align-middle">
                          <CommitInput
                            value={l.quantity != null ? String(l.quantity) : ''}
                            aria-label="数量"
                            inputMode="numeric"
                            placeholder="数量"
                            onCommit={(t) => commitQuantity(l.key, t)}
                            className={`${paperField} text-right tabular-nums`}
                          />
                        </td>
                        <td className="text-right p-2 align-middle tabular-nums">
                          {l.fromCatalog ? (
                            <span title="製品リストの価格">
                              {formatYen(l.priceExcludingTax ?? 0)}
                            </span>
                          ) : (
                            <CommitInput
                              value={
                                l.priceExcludingTax != null ? formatYen(l.priceExcludingTax) : ''
                              }
                              aria-label="単価（税抜）"
                              inputMode="numeric"
                              placeholder="単価"
                              onCommit={(t) => commitPrice(l.key, t)}
                              className={`${paperField} text-right tabular-nums`}
                            />
                          )}
                        </td>
                        <td className="text-right p-2 align-middle tabular-nums">
                          {a ? (
                            formatYen(a.subtotalIncludingTax)
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={4} className="px-2 pt-2 pb-2.5">
                      <button
                        type="button"
                        onClick={addLine}
                        className="px-1 py-0.5 rounded text-xs text-[#2b62a0] hover:bg-[#eef3fa]"
                      >
                        ＋ 品目を追加
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  {!state.hideDiscounts && totals.discountTotal > 0 && (
                    <>
                      <tr className="border-t border-black">
                        <td colSpan={3} className="p-2 text-right">
                          割引前小計 (税抜)
                        </td>
                        <td className="text-right p-2 tabular-nums">
                          {formatYen(totals.subtotalExcl + totals.discountTotal)}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <td colSpan={3} className="p-2 text-right">
                          割引合計 (税抜)
                        </td>
                        <td className="text-right p-2 tabular-nums">
                          −{formatYen(totals.discountTotal)}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr className="border-y border-black">
                    <td colSpan={3} className="p-2 text-right">
                      小計 (税抜)
                    </td>
                    <td className="text-right p-2 tabular-nums">
                      {formatYen(totals.subtotalExcl)}
                    </td>
                  </tr>
                  <tr className="border-b border-black">
                    <td colSpan={3} className="p-2 text-right">
                      消費税
                    </td>
                    <td className="text-right p-2 tabular-nums">{formatYen(totals.tax)}</td>
                  </tr>
                  {totals.tradeInDiscount > 0 && (
                    <tr className="border-b border-black">
                      <td colSpan={3} className="p-2 text-right">
                        下取り値引
                      </td>
                      <td className="text-right p-2 tabular-nums">
                        −{formatYen(totals.tradeInDiscount)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b-2 border-black bg-gray-50">
                    <td colSpan={3} className="p-2 text-right font-bold">
                      合計 (税込)
                    </td>
                    <td className="text-right p-2 tabular-nums font-bold">
                      {formatYen(totals.totalIncl)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {zeroTotalWithDiscount && (
              <p className="mb-2 text-xs text-[#b42318]">
                値引きにより合計が ¥0
                になっています。値引き欄に商品価格を入力していないかご確認ください
              </p>
            )}

            {/* 支払方法・下取り・備考 */}
            <div
              role="button"
              tabIndex={0}
              data-panel-trigger
              aria-label="支払方法・下取り・備考を編集"
              onClick={() => openZone('terms')}
              onKeyDown={(e) => zoneKeyDown(e, 'terms')}
              className={`mt-5 text-sm space-y-1 ${zoneClass(false, panel?.kind === 'terms')}`}
            >
              <p>
                <span className="font-semibold">支払方法:</span>{' '}
                {state.paymentMethod === 'transfer' ? '銀行振込' : 'クレジットカード'}
                {state.paymentMethod === 'credit_card' ? ` (${state.installments}回払い)` : ''}
              </p>
              {state.hasTradeIn ? (
                <p>
                  <span className="font-semibold">下取り:</span> あり (
                  {state.tradeInType === 'discount' ? '割引' : 'キャッシュバック'})
                  {state.tradeInAmount ? ` ${formatYen(state.tradeInAmount)}` : ''}
                </p>
              ) : (
                <p className="text-xs text-[#2b62a0]">下取り: なし（書類には表示されません）</p>
              )}
              {state.remarks ? (
                <div className="text-xs text-gray-700 border-t pt-3 !mt-3">
                  <p className="font-semibold mb-1">備考</p>
                  <p className="whitespace-pre-wrap">{state.remarks}</p>
                </div>
              ) : (
                <div className="!mt-3 px-2.5 py-2 rounded-[3px] text-xs text-[#a35f0e] outline outline-[1.5px] outline-dashed outline-[#d97706] bg-[#fff8ec]">
                  備考: 未入力
                </div>
              )}
            </div>
          </div>
        </div>

        <SidePanel
          ref={panelRef}
          open={!!panel}
          keepFocus={panel?.kind === 'product'}
          title={
            panel?.kind === 'product'
              ? '品目を選ぶ'
              : panel?.kind === 'billing'
                ? '請求先住所'
                : panel?.kind === 'delivery'
                  ? `${contactKind === 'btob' ? 'ご納品先' : 'お届け先'}（納品先住所）`
                  : '支払方法・下取り・備考'
          }
          onClose={closePanel}
        >
          {panel?.kind === 'product' && (
            <ProductPanel
              catalogs={visibleCatalogs}
              selectedCatalogId={selectedCatalogId}
              onSelectCatalog={setSelectedCatalogId}
              items={catalogItems.data ?? []}
              isLoading={activeCatalogs.isLoading || catalogItems.isLoading}
              noActiveCatalog={
                !activeCatalogs.isLoading && (activeCatalogs.data ?? []).length === 0
              }
              primaryCategoryName={
                primaryCategory && (primaryCategory.linkedBrandNames?.length ?? 0) > 0
                  ? primaryCategory.name
                  : null
              }
              isFiltered={isFiltered}
              showAllCatalogs={showAllCatalogs}
              onToggleShowAll={() => setShowAllCatalogs((v) => !v)}
              query={productQuery}
              currentName={productLine?.name ?? ''}
              currentItemId={productLine?.itemId ?? null}
              onPick={pickProduct}
              onUseCustom={blurActiveElement}
            />
          )}
          {(panel?.kind === 'billing' || panel?.kind === 'delivery') && (
            <AddressPanel
              key={panel.kind}
              kind={panel.kind}
              list={panel.kind === 'billing' ? billingList : deliveryList}
              selectedValue={addressSelection(panel.kind).value}
              savedElsewhere={addressSelection(panel.kind).savedElsewhere}
              canEdit={canEditContact && !!contact}
              isLoading={contactQuery.isLoading}
              onSelect={(v) => selectAddress(panel.kind as AddressKind, v)}
              onSave={(entry) => saveAddress(panel.kind as AddressKind, entry)}
            />
          )}
          {panel?.kind === 'terms' && (
            <TermsPanel
              value={state}
              onChange={(patch) => change((s) => ({ ...s, ...patch }))}
              onCommitTradeInAmount={(t) => {
                const raw = t.trim();
                const n = parseNumberInput(raw);
                const cur = stateRef.current.tradeInAmount;
                const next = raw === '' ? null : n !== null && n >= 0 ? Math.round(n) : cur;
                if (next !== cur) change((s) => ({ ...s, tradeInAmount: next }));
              }}
              onCommitRemarks={(t) => {
                const remarks = t.trim();
                if (remarks !== stateRef.current.remarks) change((s) => ({ ...s, remarks }));
              }}
            />
          )}
        </SidePanel>
      </div>
    </div>
  );
}

/** 上部バーの書類設定スイッチ (製品型番の表示 / 割引金額を表示)。 */
function HeaderSwitch({
  label,
  checked,
  title,
  onToggle,
}: {
  label: string;
  checked: boolean;
  title: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      onClick={onToggle}
      className="inline-flex items-center gap-2 text-[13px] text-[var(--text-primary)]"
    >
      <span
        className={`relative w-8 h-[18px] rounded-full transition-colors ${
          checked ? 'bg-[var(--accent)]' : 'bg-[var(--bg-hover)]'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 size-3.5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-3.5' : ''
          }`}
        />
      </span>
      {label}
    </button>
  );
}
